import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAcademySlug, useAcademySlugResolving } from '../../hooks/useAcademySlug';
import { useAcademyPaths } from '../../hooks/useAcademyPaths';
import { getFocusTerm, getPublicBookableTerms } from '@court-sync/shared';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '@/components/ui';
import { Badge } from '../../components/ui/Badge';
import { useFormatDate } from '../../hooks/useFormatDate';
import { AcademyBrandingProvider } from '../../contexts/AcademyBrandingContext';
import { useAuthStore } from '../../store/auth';
import { usePublicBookingStore } from '../../store/publicBooking';
import { useAcademyHost } from '../../contexts/AcademyHostContext';
import { usePublicCatalog } from '../../queries/publicCatalog';
import {
  appendStudentInviteQuery,
  publicBookingBasePath,
  resolveManualInviteTokenForNavigation,
} from './publicBooking/bookingPaths';
import { Circle, MapPin, LogIn, UserPlus } from 'lucide-react';
import { PublicAcademyContactFooter } from '../../components/public/PublicAcademyContactFooter';
import { PublicOrgSubpageHero } from '../../components/public/PublicOrgSubpageHero';
import { PublicLessonBrowser } from '../../components/public/PublicLessonBrowser';
import { isStarterMemberAuthBlockedAcademy, isStarterRequestBookingAcademy } from '../../utils/publicBrowseMode';
import { useEmbedHost } from '../../contexts/EmbedHostContext';

const shortDateOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

export interface PublicLessonsPageProps {
  /** Render only the lesson browser for use inside guardian dashboard (no public chrome). */
  embedInDashboard?: boolean;
  /** Skip nav/branding when rendered inside {@link PublicOrgLayout} (org unified public site). */
  suppressPublicChrome?: boolean;
}

export default function PublicLessonsPage({
  embedInDashboard: embedInDashboardProp,
  suppressPublicChrome: suppressPublicChromeProp,
}: PublicLessonsPageProps = {}) {
  const embedInDashboard = embedInDashboardProp === true;
  const suppressPublicChrome = suppressPublicChromeProp === true;
  const { isEmbedMode } = useEmbedHost();
  const minimalChrome = embedInDashboard || isEmbedMode;
  const showPublicChrome = !minimalChrome && !suppressPublicChrome;
  const showPageHero = !minimalChrome;
  const academySlug = useAcademySlug();
  const academySlugResolving = useAcademySlugResolving();
  const { host, academy: hostAcademy, loading: hostLoading } = useAcademyHost();
  const { locationId: locationIdFromPath } = useParams<{ locationId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const captureManualInviteFromSearchParams = usePublicBookingStore(
    (s) => s.captureManualInviteFromSearchParams
  );
  const { user } = useAuthStore();

  useEffect(() => {
    captureManualInviteFromSearchParams(searchParams);
  }, [searchParams, captureManualInviteFromSearchParams]);

  const resolvedManualInviteToken = useMemo(
    () => resolveManualInviteTokenForNavigation(searchParams, manualInviteToken),
    [searchParams, manualInviteToken]
  );

  const {
    academy,
    lessons,
    terms,
    academySports,
    ageGroups,
    publicLocations,
    publicPrograms,
    proRataPreviewByLessonId,
    isLoading: loading,
    error,
  } = usePublicCatalog({
    slug: academySlug,
    subdomainLabel: host.mode === 'subdomain' ? host.subdomainLabel : undefined,
    seedAcademy: host.mode === 'subdomain' ? hostAcademy : undefined,
    hostLoading: host.mode === 'subdomain' && hostLoading,
    includeCoaches: false,
    enabled: !!academySlug || (host.mode === 'subdomain' && !!host.subdomainLabel),
  });

  const { formatDate } = useFormatDate({ region: academy?.region, publicContext: true });
  const { p, login } = useAcademyPaths(academy);
  const bannerUrl = academy?.bannerUrl;

  const focusTerm = useMemo(() => getFocusTerm(terms, new Date()), [terms]);
  const bookableTerms = useMemo(() => getPublicBookableTerms(terms, new Date()), [terms]);

  const showMultiLocationPublic = publicLocations.length >= 2 && !!academy;

  const isLocationLanding = Boolean(
    showMultiLocationPublic && !locationIdFromPath && academySlug && !minimalChrome
  );

  /** Legacy: `/lessons?location=` → `/location/:id` */
  useEffect(() => {
    const id = searchParams.get('location');
    if (!id || !academySlug) return;
    if (!showMultiLocationPublic) return;
    if (!publicLocations.some((l) => l.id === id)) return;
    navigate(
      appendStudentInviteQuery(p(`/location/${id}`), resolvedManualInviteToken),
      { replace: true }
    );
  }, [
    searchParams,
    showMultiLocationPublic,
    publicLocations,
    academySlug,
    navigate,
    resolvedManualInviteToken,
    p,
  ]);

  /** `/location/:id` when multi-location is off or id is unknown → `/lessons` */
  useEffect(() => {
    if (!locationIdFromPath || !academySlug || loading) return;
    if (!showMultiLocationPublic) {
      navigate(
        appendStudentInviteQuery(p('/lessons'), resolvedManualInviteToken),
        { replace: true }
      );
      return;
    }
    if (!publicLocations.some((l) => l.id === locationIdFromPath)) {
      navigate(
        appendStudentInviteQuery(p('/lessons'), resolvedManualInviteToken),
        { replace: true }
      );
    }
  }, [
    locationIdFromPath,
    publicLocations,
    showMultiLocationPublic,
    academySlug,
    navigate,
    loading,
    resolvedManualInviteToken,
    p,
  ]);

  const handleBookLesson = (lessonId: string) => {
    if (!academySlug) return;
    navigate(
      appendStudentInviteQuery(
        publicBookingBasePath(academy ?? { slug: academySlug }, lessonId),
        resolvedManualInviteToken
      )
    );
  };

  const publicLoginReturnUrl = appendStudentInviteQuery(p('/lessons'), resolvedManualInviteToken);

  const lessonsPageHeading = useMemo(() => {
    let base = 'Available lessons';
    if (locationIdFromPath) {
      const loc = publicLocations.find((l) => l.id === locationIdFromPath);
      if (loc) base = loc.name;
    } else if (publicLocations.length === 1) {
      base = publicLocations[0].name;
    }
    return base;
  }, [locationIdFromPath, publicLocations]);

  if (loading || academySlugResolving) {
    if (minimalChrome) {
      return (
        <div className="flex justify-center py-16" aria-busy="true">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
          <p className="text-muted-foreground">Loading lessons...</p>
        </div>
      </div>
    );
  }

  if (error || !academy) {
    if (minimalChrome) {
      return (
        <Card className="border-destructive/40">
          <CardContent className="p-8 text-center">
            <Circle className="mx-auto mb-3 h-12 w-12 text-muted-foreground" aria-hidden />
            <h2 className="mb-1 text-lg font-semibold text-foreground">Could not load lessons</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              {error || 'The academy you are looking for does not exist.'}
            </p>
            <Button type="button" variant="outline" onClick={() => navigate('/academy-picker')}>
              Back to academy picker
            </Button>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <Circle className="h-16 w-16 text-muted-foreground" />
          </div>
          <h1 className="mb-2 text-2xl font-bold text-foreground">Academy Not Found</h1>
          <p className="mb-4 text-muted-foreground">
            {error || 'The academy you are looking for does not exist.'}
          </p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  const starterMemberAuthBlocked = academy ? isStarterMemberAuthBlockedAcademy(academy) : false;
  const starterRequestBooking = academy ? isStarterRequestBookingAcademy(academy) : false;

  const pageBody = (
    <>
      <div
        className={
          minimalChrome || suppressPublicChrome
            ? 'flex min-h-0 w-full min-w-0 flex-1 flex-col'
            : 'flex min-h-screen flex-col bg-background'
        }
      >
        {showPublicChrome && (
          <nav className="border-b border-border bg-card shadow-sm">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 justify-between">
                <div className="flex items-center">
                  <Link
                    to={appendStudentInviteQuery(p(''), resolvedManualInviteToken)}
                    className="flex items-center"
                  >
                    {academy.logoUrl ? (
                      <img
                        src={academy.logoUrl}
                        alt={academy.name}
                        className="h-10 w-auto object-contain md:h-12"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="text-xl font-bold gradient-text">{academy.name}</span>
                    )}
                  </Link>
                </div>
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate(appendStudentInviteQuery(p('/lessons'), resolvedManualInviteToken))
                    }
                    className="rounded-md px-4 py-2 text-sm font-medium"
                  >
                    View Lessons
                  </Button>
                  {starterMemberAuthBlocked ? null : user?.roles?.includes('guardian') ? (
                    <Button
                      variant="default"
                      onClick={() => navigate(p('/dashboard'))}
                      className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium"
                      style={
                        academy.primaryColor ? { backgroundColor: academy.primaryColor } : undefined
                      }
                    >
                      <LogIn className="h-4 w-4" />
                      Student Dashboard
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={() => navigate(login(publicLoginReturnUrl))}
                      className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium"
                      style={
                        academy.primaryColor ? { backgroundColor: academy.primaryColor } : undefined
                      }
                    >
                      <LogIn className="h-4 w-4" />
                      Student Login
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </nav>
        )}

        <div className="flex min-h-0 flex-1 flex-col">
          {isLocationLanding ? (
            <>
              {showPageHero ? (
                <PublicOrgSubpageHero
                  bannerUrl={bannerUrl}
                  title="Choose a location"
                  headingId="public-lessons-location-hero-heading"
                  backLink={{
                    to: appendStudentInviteQuery(p(''), resolvedManualInviteToken),
                    label: 'Back to academy home',
                  }}
                >
                  <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
                    Select a site to see camps and lessons. You can switch between list and schedule
                    views after you choose.
                  </p>
                </PublicOrgSubpageHero>
              ) : null}
              {embedInDashboard && !isEmbedMode && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-foreground">Choose a location</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Select a site to browse camps and lessons. You can switch between list and weekly
                    views after you choose.
                  </p>
                </div>
              )}
              <div
                className={
                  minimalChrome
                    ? 'mx-auto flex w-full max-w-7xl flex-1 flex-col'
                    : 'mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 py-8 sm:px-6 lg:px-8'
                }
              >
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {publicLocations.map((loc) => (
                    <Card key={loc.id} className="border-border">
                      <CardContent className="flex flex-col gap-3 p-6">
                        <MapPin className="h-8 w-8 shrink-0 text-primary-600" aria-hidden />
                        <h2 className="text-lg font-semibold text-foreground">{loc.name}</h2>
                        <p className="text-sm text-muted-foreground">
                          {loc.address || loc.googleMapsAddress || '—'}
                        </p>
                        <Button
                          asChild
                          className="mt-auto w-full"
                          style={
                            academy.primaryColor
                              ? { backgroundColor: academy.primaryColor }
                              : undefined
                          }
                        >
                          <Link
                            to={appendStudentInviteQuery(
                              p(`/location/${encodeURIComponent(loc.id)}`),
                              resolvedManualInviteToken
                            )}
                          >
                            Browse lessons at this location
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {showPageHero ? (
                <PublicOrgSubpageHero
                  bannerUrl={bannerUrl}
                  title={lessonsPageHeading}
                  headingId="public-lessons-hero-heading"
                  backLink={
                    suppressPublicChrome
                      ? {
                          to: appendStudentInviteQuery(p(''), resolvedManualInviteToken),
                          label: 'Back to academy home',
                        }
                      : undefined
                  }
                >
                    {bookableTerms.length > 1 ? (
                      <p className="mt-3 max-w-3xl text-sm text-white/90">
                        Showing lessons across{' '}
                        <span className="font-medium text-white">{bookableTerms.length} terms</span>
                        {focusTerm ? (
                          <>
                            {' '}
                            — current focus:{' '}
                            <span className="font-medium text-white">{focusTerm.name}</span>
                          </>
                        ) : null}
                      </p>
                    ) : focusTerm ? (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <p className="max-w-3xl text-sm text-white/90">
                          Showing term:{' '}
                          <span className="font-medium text-white">{focusTerm.name}</span>
                          {' · '}
                          {formatDate(focusTerm.startDate, academy.region, shortDateOptions)} –{' '}
                          {formatDate(focusTerm.endDate, academy.region, shortDateOptions)}
                        </p>
                        {(() => {
                          const now = new Date();
                          const start = new Date(focusTerm.startDate);
                          const end = new Date(focusTerm.endDate);
                          const inSession = now >= start && now <= end;
                          return (
                            <Badge
                              variant="secondary"
                              className={
                                inSession
                                  ? 'border-transparent bg-green-600 text-white'
                                  : 'border-transparent bg-primary-600 text-white'
                              }
                            >
                              {inSession ? 'Current term' : 'Upcoming term'}
                            </Badge>
                          );
                        })()}
                      </div>
                    ) : null}
                    {!focusTerm && bookableTerms.length === 0 && terms.length > 0 ? (
                      <p className="mt-3 max-w-3xl text-sm text-white/85">
                        No current or upcoming term — group term lessons are hidden; camps may still be
                        available below.
                      </p>
                    ) : null}
                </PublicOrgSubpageHero>
              ) : null}

              <div
                className={
                  minimalChrome
                    ? 'mx-auto w-full max-w-7xl flex-1'
                    : 'mx-auto w-full max-w-7xl flex-1 px-4 py-5 sm:px-6 sm:py-6 lg:px-8'
                }
              >
                {academy.privateBookingEnabled && !starterRequestBooking && !minimalChrome && (
                  <div className="mb-8 flex flex-col gap-3 rounded-lg border border-primary/25 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
                        <UserPlus className="h-5 w-5 text-primary" aria-hidden />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-foreground">
                          Book a private lesson
                        </h2>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          Pick a coach, weekly time, and number of sessions — no pre-listed lesson
                          required.
                        </p>
                      </div>
                    </div>
                    <Button asChild className="w-full shrink-0 sm:w-auto">
                      <Link
                        to={appendStudentInviteQuery(p('/book-private'), resolvedManualInviteToken)}
                      >
                        Book private lesson
                      </Link>
                    </Button>
                  </div>
                )}

                <PublicLessonBrowser
                  academy={academy}
                  academySlug={academySlug!}
                  lessons={lessons}
                  terms={terms}
                  academySports={academySports}
                  ageGroups={ageGroups}
                  publicLocations={publicLocations}
                  publicPrograms={publicPrograms}
                  proRataPreviewByLessonId={proRataPreviewByLessonId}
                  resolvedManualInviteToken={resolvedManualInviteToken}
                  onBookLesson={handleBookLesson}
                  buildAcademyPath={p}
                  lockedLocationId={locationIdFromPath}
                />
              </div>
            </>
          )}
          {!minimalChrome && (
            <PublicAcademyContactFooter
              academy={academy}
              showMultiLocationPublic={showMultiLocationPublic}
            />
          )}
        </div>
      </div>
    </>
  );

  if (minimalChrome || suppressPublicChrome) {
    return pageBody;
  }

  return (
    <AcademyBrandingProvider
      primaryColor={academy.primaryColor}
      secondaryColor={academy.secondaryColor}
    >
      {pageBody}
    </AcademyBrandingProvider>
  );
}
